import { DataAccess } from "../dataaccess/data.access";
import { Cache } from "../decorators/cache.decorator";
import { Timeout } from "../decorators/timeout.decorator";
import { jSith } from "../util/jquery-replacement";
import { S25Util } from "../util/s25-util";
import { EventIncludeOption, EventService } from "./event.service";
import {
    Invoice,
    InvoiceUpdateData,
    EventPricingData,
    LineItem,
    LineItemI,
    MicroBilling,
    RateGroupListItem,
    TaxListItem,
    lineItemType,
    InvoiceNamesAndIds,
    LineItemType,
    LineItemAdjData,
    PricingOccurrence,
    UpdateData,
    RateSchedule,
    Occurrence,
    InvoiceSortGroup,
    InvoiceSort,
} from "../pojo/Pricing";
import { Proto } from "../pojo/Proto";
import ISODateString = Proto.ISODateString;
import { TelemetryService } from "./telemetry.service";
import NumericalString = Proto.NumericalString;
import { ICheckoutRequest, PaymentService } from "../modules/s25-pricing/s25-payments/payment.service";
import { RsrvIdMap, S25PricingUtil } from "../modules/s25-pricing/s25.pricing.util";
import { FormatService } from "./format.service";
import { S25PricingStore } from "../modules/s25-pricing/s25.pricing.store";
import { EventMicroService } from "./event.micro.service";

export class PricingService {
    public static getObjUpdateData(
        invoiceId: number,
        itemId: number,
        itemType: LineItemType | "rsrv",
        objIds: RsrvIdMap,
        storeInstance: InstanceType<typeof S25PricingStore>,
        invoice?: InvoiceNamesAndIds,
    ): Partial<InvoiceUpdateData> {
        if (!invoice) {
            invoice = storeInstance.invoiceSummaryData().find((invoice) => invoice.id === invoiceId);
        }
        if (invoice && !invoice.locked) {
            const { reqIds, resourceIds, spaceIds, rsrvIds, includeEventType } = invoice;
            const rsrvIdList = [...(rsrvIds ?? []), ...(itemType === "rsrv" && !!invoiceId ? [itemId] : [])];
            const reqIdList = [
                ...(reqIds ?? []),
                ...(itemType === lineItemType.REQUIREMENT && !!invoiceId ? [itemId] : []),
            ];
            let allSpaceIds = [...(spaceIds ?? [])];
            let allResourceIds = [...(resourceIds ?? [])];
            if (objIds) {
                // for objects associated with occurrences (excluding event types), the api needs an updated list of ids for the billDefn section - doesn't get updated with line item data even though the line items make it to the invoice
                const { spaces, resources } = objIds ?? {};
                allSpaceIds = Array.from(new Set([...spaceIds, ...(spaces ?? [])]));
                allResourceIds = Array.from(new Set([...resourceIds, ...(resources ?? [])]));
            }
            return {
                billId: invoice.id,
                op: "update",
                emptyRsrvList: !rsrvIdList.length && !includeEventType,
                includeEventType: includeEventType,
                includeSpaces: rsrvIdList.length > 0,
                includeResources: rsrvIdList.length > 0,
                requirementIdList: reqIdList,
                includeRequirements: reqIdList.length > 0,
                rsrvIdList: rsrvIdList,
                ...(!!rsrvIdList.length && {
                    resourceIdList: allResourceIds,
                    spaceIdList: allSpaceIds,
                }),
            };
        }
    }

    public static getRemoveObjData(
        invoiceIdForRemoval: number,
        itemId: number,
        itemType: LineItemType | "rsrv",
        storeInstance: InstanceType<typeof S25PricingStore>,
        invoiceForRemoval?: InvoiceNamesAndIds,
    ) {
        if (!invoiceForRemoval) {
            invoiceForRemoval = storeInstance
                .invoiceSummaryData()
                .find((invoice) => invoice.id === invoiceIdForRemoval);
        }
        if (invoiceForRemoval && !invoiceForRemoval.locked) {
            const { reqIds, rsrvIds } = invoiceForRemoval;
            if (itemType === lineItemType.REQUIREMENT) {
                reqIds.has(itemId) && reqIds.delete(itemId);
            } else {
                rsrvIds.has(itemId) && rsrvIds.delete(itemId);
            }
            return this.getObjUpdateData(null, itemId, itemType, null, storeInstance, invoiceForRemoval);
        }
    }

    // handle necessary updates when moving/removing objects, ie remove occurrence from original invoice if moving to another
    public static async handleInvoiceObjectUpdates(
        dropdownId: number | string, // either an action for the current invoice ("new", "remove") or an id for the new invoice
        itemId: number,
        itemType: LineItemType | "rsrv",
        objIds: RsrvIdMap,
        evBillId: number, // for occurrences, if moving to a new invoice, need to remove it from the old invoice
        lineItemAdjustments: LineItemAdjData[],
        storeInstance: InstanceType<typeof S25PricingStore>,
    ) {
        let payloads = [];
        if (dropdownId === "remove") {
            payloads.push(PricingService.getRemoveObjData(evBillId, itemId, itemType, storeInstance));
        } else {
            payloads.push(PricingService.getObjUpdateData(+dropdownId, itemId, itemType, objIds, storeInstance, null));
            if (itemType === "rsrv") {
                payloads.push(PricingService.getRemoveObjData(evBillId, itemId, itemType, storeInstance));
            }
        }
        await PricingService.putMultiInvoices(payloads);
        //update adjustment selections after invoices are updated ANG-6224
        if (lineItemAdjustments?.length) {
            const payload = lineItemAdjustments.map((adj) => {
                if (S25Util.isDefined(adj.adjustmentPercent)) {
                    return { ...adj, adjustmentPct: adj.adjustmentPercent };
                }
                return adj;
            });
            await PricingService.putLineItemAdjustments(payload);
        }
    }

    public static getPricingOccurrenceList(listData: any, storeInstance: InstanceType<typeof S25PricingStore>) {
        let ret: any = [];
        const resMap: any = {},
            allOccsMapById = storeInstance.occMap(),
            locatorMap = storeInstance.locatorMap();

        let newRows = listData.rows.filter((row: LineItemI) => {
            row.occurrences?.forEach((occ: LineItem) => {
                const {
                    bill_item_id,
                    bill_item_name,
                    bill_item_type_id,
                    canAdjust,
                    canEdit,
                    canEditPricing,
                    evBillId,
                    charge_to_id,
                    credit_account_number,
                    debit_account_number,
                    eventId,
                    eventLocator,
                    ev_dt_profile_id,
                    rate_group_name,
                    rate_id,
                    rate_name,
                } = row;

                occ = {
                    ...occ,
                    adjustment_amt: occ.adjustmentAmt,
                    adjustment_name: occ.adjustmentName,
                    adjustment_percent: occ.adjustmentPercent,
                    bill_item_name: bill_item_name ?? row.itemName,
                    bill_item_id: bill_item_id ?? row.itemId,
                    bill_item_type_id: bill_item_type_id ?? row.itemType,
                    canAdjust,
                    canEdit,
                    canEditPricing,
                    credit_account_number: credit_account_number ?? row.creditAccountNumber,
                    debit_account_number: debit_account_number ?? row.debitAccountNumber,
                    evBillId,
                    charge_to_id: charge_to_id ?? row.chargeToId,
                    eventId,
                    eventLocator: eventLocator ?? locatorMap[eventId],
                    rate_group_name: rate_group_name ?? row.rateGroupName,
                    rate_id,
                    rate_name: rate_name ?? row.rateScheduleName,
                    allOrgs: listData.allOrgs,
                    rateGroups: listData.rateGroups,
                    profileId: ev_dt_profile_id,
                    eventData: listData.eventData,
                };
                if (!resMap[occ.rsrvId]) {
                    resMap[occ.rsrvId] = {
                        occurrence: occ.occurrence ?? allOccsMapById[occ.rsrvId]?.occurrence,
                        occView: !!listData.occView,
                        numOccurrences: row.occurrences.length,
                        listItems: [{ ...occ, isExpandedOcc: true }],
                        subtotal: listData.occSubtotals[occ.rsrvId],
                        list_price: listData.isSummary
                            ? occ.listPrice || 0
                            : listData.occSubtotals[occ.rsrvId]?.occurrenceListPrice,
                        charge_to_id: charge_to_id ?? row.chargeToId,
                        bill_item_id: bill_item_id ?? row.itemId,
                        eventLocator: eventLocator ?? locatorMap[eventId],
                        eventData: listData.eventData,
                        taxable_amt: listData.isSummary
                            ? occ.price || 0
                            : listData.occSubtotals[occ.rsrvId]?.occurrenceTotalCharge,
                        rsrvId: occ.rsrvId,
                        isSummary: listData.isSummary,
                    };
                } else {
                    resMap[occ.rsrvId].listItems.push({ ...occ, isExpandedOcc: true });
                    if (listData.isSummary) {
                        resMap[occ.rsrvId].list_price += occ.listPrice || 0;
                        resMap[occ.rsrvId].taxable_amt += occ.price || 0;
                    }
                }
            });
            const hasOccurrences = !!row.occurrences && row.occurrences.length > 0;

            if (listData.hideNoChargeItems) {
                return !hasOccurrences && !!row.total_charge;
            }

            return !hasOccurrences;
        });

        const occurrences: any = Object.values(resMap).sort(
            (a: PricingOccurrence, b: PricingOccurrence) => +new Date(a.occurrence) - +new Date(b.occurrence),
        );

        S25PricingUtil.formatOccDates(occurrences, { ...allOccsMapById, ...listData.datePrefs });

        listData.noOccItems = [...newRows];

        newRows = [...occurrences, ...newRows];
        const isNonInvoiced = listData.orgId === S25PricingUtil.NonInvoicedOrgId;
        newRows.forEach((row: LineItemI) => {
            const taxesObj: any = { tax: [] };
            let taxes = !row.occurrence && S25Util.propertyGet(row, "taxes");
            jSith.forEach(taxes, (_, tax) => {
                taxesObj.tax.push({
                    itemName: tax.tax_name ?? tax.taxName ?? listData.taxesMap[tax.tax_id],
                    itemValue: FormatService.toDollars(tax.tax_charge ?? tax.taxCharge),
                });
            });

            const itemData = {
                itemName:
                    row.occurrence ??
                    ((row.bill_item_name ?? row.itemName) || "") +
                        (row.bill_profile_name && S25Util.profileName(row.bill_profile_name.toString())
                            ? " (" + S25Util.profileName(row.bill_profile_name.toString()) + ")"
                            : ""),
                reservations: row.listItems,
                charge_to_id: row.charge_to_id ?? row.chargeToId,
                bill_item_id: row.bill_item_id ?? row.itemId,
                adjustment:
                    parseFloat((row.adjustment_amt ?? row.adjustmentAmt) || 0) +
                        parseFloat((row.adjustment_percent ?? row.adjustmentPercent) || 0) *
                            (row.taxable_amt ?? row.price) || 0,
                total_tax: row.total_tax ?? row.tax,
                total_charge: row.total_charge ?? row.total,
                dateFormat: listData.dateFormat,
                noOccItems: listData.noOccItems,
                isNonInvoiced: isNonInvoiced,
            };
            const billNameMap = storeInstance.billNameMap();
            const identifier =
                row.bill_item_type_id === 1 || row.itemType === 1
                    ? "eventType"
                    : (row.rsrvId ?? row.bill_item_id ?? row.itemId);
            const setNames = !!billNameMap[identifier] ? Array.from(billNameMap[identifier]).join(", ") : "";

            let tableRow = [
                itemData,
                (row.list_price ?? row.listPrice) || 0,
                Object.assign({}, row, {
                    adjustmentType: 1,
                    itemName:
                        parseFloat((row.adjustment_amt ?? row.adjustmentAmt) || 0) +
                        parseFloat((row.adjustment_percent ?? row.adjustmentPercent) || 0),
                    eventData: listData.eventData,
                    occView: !!listData.occView,
                    noOccItems: listData.noOccItems,
                    isNonInvoiced: isNonInvoiced,
                }), //itemName used for sorting
                (row.taxable_amt ?? row.price) || 0,
                taxesObj,
                (row.total_charge ?? row.taxable_amt ?? row.total) || 0,
                S25Util.merge({ eventData: listData.eventData }, row, {
                    allOrgs: listData.allOrgs,
                }),
                row.rate_name ?? row.rateScheduleName ?? "",
                S25Util.merge({ eventData: listData.eventData }, row, {
                    rateGroups: listData.rateGroups,
                    itemName: row.rate_group_name ?? row.rateGroupName,
                    fls: listData.fls,
                    isSummary: listData.isSummary,
                }),
                row.debit_account_number ?? row.debitAccountNumber ?? "",
                row.credit_account_number ?? row.creditAccountNumber ?? "",
            ];
            listData.combineRelatedEvents && tableRow.unshift(row.eventLocator ?? locatorMap[row.eventId]); // add event locator to reference column
            listData.isSummary && tableRow.splice(listData.combineRelatedEvents ? 7 : 6, 0, setNames); // Invoice To column data if payment mode/summary view

            ret.push({
                itemName: itemData.itemName, //used for initial sort
                isNonInvoiced: isNonInvoiced,
                row: tableRow,
            });
        });

        if (ret.length === 0) {
            //at least one row (an empty one) so that list still shows since footer may have data
            ret.push({ row: ["(none)", "0", "", "0", "", "0", "", "", "", "", ""] });
        }

        return ret;
    }

    public static processTableUpdate(
        data: any,
        model: any,
        storeInstance: InstanceType<typeof S25PricingStore>,
    ): UpdateData {
        // trigger refresh if related events to ensure accurate data
        if (data.content?.data?.items?.[0]?.billDefn?.events?.length > 1) return null;
        const { lineItems, subtotals, totals } = data.content?.data?.items[0]?.billing;
        const adjustments = data.content?.data.items[0]?.billing?.adjustments;
        const { occurrences, rateSchedules, profiles } = data.content?.expandedInfo;
        const { canAdjust, canEdit, canEditPricing, evBillId, eventId } = model;

        let occNames: any;
        if (occurrences) {
            occNames = S25Util.fromEntries(occurrences?.map((occ: PricingOccurrence) => [occ.rsrvId, occ.occurrence]));
        }

        const itemsMap: any = {
            subtotals: subtotals[0]?.account,
            adjustments,
            taxData: {},
            combineRelatedEvents: model.combineRelatedEvents,
        };

        const eventLocatorMap = storeInstance.locatorMap();

        lineItems.map((item: any) => {
            if (
                model.adjustmentType === 1 &&
                (S25Util.isDefined(item.adjustmentAmt) || S25Util.isDefined(item.adjustmentPercent)) &&
                item.itemId === model.bill_item_id &&
                item.itemType === model.bill_item_type_id
            ) {
                storeInstance.updateLineItemAdjustment(item, evBillId);
            }
            const profileName =
                !!item.profileId &&
                S25Util.profileName(
                    (
                        profiles?.find(
                            (profile: { profileId: number; name: string }) => profile.profileId === item.profileId,
                        )?.name ?? ""
                    ).toString(),
                );
            const row = {
                adjustment_amt: item.adjustmentAmt,
                adjustment_percent: item.adjustmentPercent / 100,
                adjustment_name: item.adjustmentName,
                canAdjust,
                canEdit,
                canEditPricing,
                charge_to_id: item.chargeToId,
                charge_to_name: model.orgNameMap ? model.orgNameMap[item.chargeToId] : "",
                evBillId,
                originalRateGroupId: model.rate_group_id,
                credit_account_number: item.creditAccountNumber,
                debit_account_number: item.debitAccountNumber,
                bill_item_id: item.itemId,
                bill_item_name: `${item.itemName}${profileName ? ` (${profileName})` : ""}`,
                bill_item_type_id: item.itemType,
                list_price: item.listPrice ?? 0,
                taxable_amt: item.price,
                eventId,
                noOccItems: model.noOccItems,
                ev_dt_profile_id: item.profileId,
                rate_group_id: item.rateGroupId,
                rate_group_name:
                    model.rateGroups?.find((group: any) => group.rate_group_id === item.rateGroupId)?.rate_group_name ??
                    model.rate_group_name,
                rate_id: item.rateId,
                rate_name:
                    rateSchedules?.find((schedule: RateSchedule) => schedule.rateId === item.rateId)?.rateName ?? "",
                total_tax: item.tax,
                deleteId: data.deleteId,
                occurrences: item.occurrences?.map((occ: any) => ({ ...occ, occurrence: occNames[occ.rsrvId] })),
                isNew: data.isNew,
                prevTax: model.tax,
                tax: item.taxes.map((tax: any) => {
                    const taxObj = {
                        tax_charge: tax.taxCharge,
                        tax_id: tax.taxId,
                        tax_name: tax.taxName,
                    };

                    if (itemsMap.taxData[taxObj.tax_id]) {
                        itemsMap.taxData[taxObj.tax_id].tax_charge += taxObj.tax_charge;
                        itemsMap.taxData[taxObj.tax_id].itemValue = S25PricingUtil.formatCurrency(
                            itemsMap.taxData[taxObj.tax_id].tax_charge,
                        );
                    } else {
                        itemsMap.taxData[taxObj.tax_id] = {
                            tax_id: taxObj.tax_id,
                            tax_charge: taxObj.tax_charge,
                            itemValue: S25PricingUtil.formatCurrency(taxObj.tax_charge),
                            itemName: taxObj.tax_name,
                        };
                    }

                    return taxObj;
                }),
                total_charge: item.total,
            };

            if (itemsMap[item.chargeToId]) {
                itemsMap[item.chargeToId].rows = itemsMap[item.chargeToId].rows.filter(
                    (billItem: any) =>
                        billItem.bill_item_type_id > 0 &&
                        (billItem.bill_item_id !== item.itemId || billItem.ev_dt_profile_id !== item.profileId) &&
                        billItem.charge_to_id === item.chargeToId,
                );
                itemsMap[item.chargeToId].rows.push(row);
            } else {
                itemsMap[item.chargeToId] = {
                    rows: [
                        row,
                        ...model.eventData.bill_item.filter((billItem: any) => {
                            billItem.canAdjust = canAdjust;
                            billItem.canEdit = canEdit;
                            billItem.canEditPricing = canEditPricing;
                            billItem.evBillId = evBillId;

                            return (
                                eventLocatorMap[billItem.eventId] &&
                                (billItem.bill_item_id !== item.itemId ||
                                    billItem.ev_dt_profile_id !== item.profileId) &&
                                billItem.charge_to_id === item.chargeToId &&
                                billItem.bill_item_type_id > 0
                            );
                        }),
                    ],
                    footerData: {},
                };
            }

            return row;
        });

        subtotals[0].accountOccurrence?.map((occ: any) => {
            if (!itemsMap[occ.chargeToId].occSubtotals) {
                itemsMap[occ.chargeToId].occSubtotals = { [occ.rsrvId]: occ };
            } else {
                itemsMap[occ.chargeToId].occSubtotals[occ.rsrvId] = occ;
            }
        });

        let index: number;

        if (model.occView && !model.isOccurrence && !model.allBillItems) {
            // occurrences updated, but non-occurrence line Items have to be updated if adjusted
            let changedItem = model.noOccItems.find((item: LineItemI, i: number) => {
                index = i;
                return item.bill_item_id === model.bill_item_id;
            });
            const newValue = lineItems.find((item: LineItem) => item.itemId === changedItem.bill_item_id);

            changedItem = {
                ...changedItem,
                adjustment_amt: newValue.adjustmentAmt,
                adjustment_percent: newValue.adjustmentPercent,
                adjustment_name: newValue.adjustmentName,
                tax: newValue.taxes,
                taxable_amt: newValue.price,
                list_price: newValue.listPrice,
                total_charge: newValue.total,
            };
            model.noOccItems[index] = changedItem;
        }

        const nonOccPrice =
            model.occView &&
            model.noOccItems.reduce((sum: number, item: LineItemI) => sum + (item.taxable_amt ?? 0), 0);
        const filteredTax =
            model.occView && model.noOccItems.reduce((sum: number, item: LineItemI) => sum + (item.total_tax ?? 0), 0);

        subtotals[0].account.map((item: any) => {
            const listPrice = (
                item.requirementsListPrice +
                item.occurrenceListPrice +
                (model.occView ? 0 : item.occurrenceAdjustments)
            ).toString();

            const data = {
                subtotal: {
                    id: item.chargeToId,
                    footerRow: true,
                    row: [
                        "Subtotal",
                        listPrice,
                        S25PricingUtil.formatCurrency(
                            model.occView
                                ? item.occurrenceAdjustments + S25PricingUtil.agg.adjustments.subtotal(model.noOccItems)
                                : item.profileAdjustments + item.requirementsAdjustments,
                        ),
                        (model.occView ? nonOccPrice + item.occurrenceTotalCharge : item.taxableAmount) //     ? item.occurrenceTotalCharge + item.requirementsTotalCharge + noOccTotalCharge // (model.occView
                            ?.toString(),
                        S25PricingUtil.formatCurrency(model.occView ? filteredTax : item.tax),
                        (model.occView
                            ? nonOccPrice + item.occurrenceTotalCharge + filteredTax
                            : item.eventsTotalCharge
                        ).toString(),
                        ...Array(5).fill(""),
                    ],
                },
                total: {
                    id: item.chargeToId,
                    footerRow: true,
                    row: [
                        "Total",
                        listPrice,
                        S25PricingUtil.formatCurrency(
                            item.profileAdjustments +
                                item.grossAdjustments +
                                item.requirementsAdjustments +
                                (model.occView ? item.occurrenceAdjustments : 0),
                        ),
                        (item.taxableAmount + item.grossAdjustments).toString(),
                        S25PricingUtil.formatCurrency(item.tax),
                        item.grandTotal.toString(),
                        ...Array(5).fill(""),
                    ],
                },
                adjustments: S25PricingUtil.getAdjustmentRows(model, item, adjustments, subtotals, eventLocatorMap),
            };

            if (model.combineRelatedEvents) {
                data.total.row.unshift("");
                data.subtotal.row.unshift("");
            }
            if (itemsMap[item.chargeToId]) itemsMap[item.chargeToId].footerData = data;
            return data;
        });

        if (!itemsMap.totals) itemsMap.totals = totals;
        itemsMap.bill_item_id = model.bill_item_id;
        itemsMap.adjustmentId = data.billItemId;
        itemsMap.orgId = model.orgId ?? model.charge_to_id;
        itemsMap.newChargeToId = data.newChargeToId;
        itemsMap.profileSubtotals = subtotals[0].accountProfile;

        return itemsMap;
    }

    @Timeout
    @Cache({ immutable: true, targetName: "PricingService" })
    public static getRatesAndTaxes() {
        return DataAccess.get<{
            root: { rate_groups: { rate_group: RateGroupListItem[] }; taxes: { tax: TaxListItem[] } };
        }>(DataAccess.injectCaller("/pricing/rates_taxes.json", "PricingService.getRatesAndTaxes")).then((data) => {
            // Force tax rate group list into array
            for (let tax of data?.root?.taxes?.tax || []) tax.rate_groups = S25Util.array.forceArray(tax.rate_groups);
            return data;
        });
    }

    //note: pricing data is NOT all combined like it is in getPricing with combineRelatedEvents set to true (for list)
    //this just returns all events and their pricing data separated into each event from the API
    public static getPricingRelatedEvents(eventId: number, includeRelatedEvents: boolean) {
        //billing: bill items
        //customers: organizations on event
        //profile: name and date info for profile on each event
        var includes: EventIncludeOption[] = ["billing", "customers", "profile"];
        if (includeRelatedEvents) {
            return EventService.getEventRelationships(eventId).then(function (rel) {
                if (rel && rel.content && rel.content.length) {
                    var eventIds = [eventId];
                    jSith.forEach(rel.content, function (_, c) {
                        eventIds.push(c.content_event_id);
                    });
                    return EventService.getEventsInclude(eventIds, includes);
                } else {
                    return EventService.getEventsInclude(eventId, includes);
                }
            });
        } else {
            return EventService.getEventsInclude(eventId, includes);
        }
    }

    public static getPricingAndEventsFromPricingSetId(eventId: number, evBillId: number) {
        return PricingService.getPricingFromPricingSetId(eventId, evBillId).then(function (pricingEventData) {
            return EventService.getEventsInclude(pricingEventData.eventIds, ["customers", "profile"]).then(
                function (events) {
                    jSith.forEach(events, function (_, event) {
                        event.bill_item = [];
                        jSith.forEach(pricingEventData.bill_item, function (_, billItem) {
                            if (parseInt(billItem.eventId) === parseInt(event.event_id)) {
                                event.bill_item.push(billItem);
                            }
                        });
                    });
                    return events;
                },
            );
        });
    }

    public static getPricingFromPricingSetId(
        eventId: number,
        evBillId: number,
        combineRelatedEvents?: boolean,
        storeInstance?: InstanceType<typeof S25PricingStore>,
    ) {
        return PricingService.getPricingSet(evBillId, storeInstance).then(function (pricingSet) {
            return PricingService.getPricingFromPricingSet(eventId, pricingSet, combineRelatedEvents, storeInstance);
        });
    }

    public static getPricingFromPricingSet(
        eventId: number,
        pricingSet?: MicroBilling,
        combineRelatedEvents?: boolean,
        storeInstance?: InstanceType<typeof S25PricingStore>,
    ) {
        if (!pricingSet?.data?.items?.[0]) return;

        let { rateGroups, rateSchedules, organizations, profiles, occurrences } = pricingSet.expandedInfo;
        const bill = pricingSet.data.items[0];
        const { billDefn, billing, id } = bill;

        if (!combineRelatedEvents) {
            const rsrvIds = billDefn.reservations
                .filter((rsrv) => rsrv.event_id === eventId)
                .map((rsrv) => rsrv.rsrv_id);
            occurrences = occurrences.filter((occ) => rsrvIds.includes(occ.rsrvId));
            billing.lineItems = billing.lineItems.filter((lineItem) => lineItem.eventId === eventId);
        }

        const groupNames = S25Util.fromEntries(rateGroups.map((group) => [group.rateGroup_id, group.rateGroupName]));
        const rateNames = S25Util.fromEntries(rateSchedules.map((schedule) => [schedule.rateId, schedule.rateName]));
        const orgNames = S25Util.fromEntries(organizations.map((org) => [org.organizationId, org.organizationName]));
        const profileNames = S25Util.fromEntries(profiles.map((profile) => [profile.profileId, profile.name]));
        const occNames = S25Util.fromEntries(occurrences.map((occ) => [occ.rsrvId, occ.occurrence]));

        let ret: any = {
            event_id: eventId,
            eventIds: S25Util.propertyGetAllUnique(bill, "eventId").filter(function (id: any) {
                return id > 0;
            }),
            eventsNeedingRefresh: [],
            organization: [],
            orgToEvents: {},
            event_history: [
                {
                    history_type_id: 4,
                    history_dt: billDefn.billDate,
                },
            ],
            bill_item: [],
            evBillId: id,
            occSubtotals: billing.subtotals[0].accountOccurrence,
            occurrences: occurrences,
            profileSubtotals: billing.subtotals[0].accountProfile,
            noOccs: !billDefn.includeSpaces && !billDefn.includeResources && !billDefn.includeEventType,
            content: [],
        };

        return S25Util.all({
            eventsNeedingRefresh: EventService.getEventsNeedingRefresh(ret.eventIds),
            allEventData: EventService.getEventsInclude(ret.eventIds, ["customers", "relationships"]),
        }).then(function (resp) {
            combineRelatedEvents && storeInstance?.setLocatorMap(resp.allEventData);
            let eventIdMap: any = {};

            ret.eventsNeedingRefresh = resp.eventsNeedingRefresh;

            jSith.forEach(resp.allEventData, function (_, event) {
                eventIdMap[event.event_id] = event;
                event.content && ret.content.push(event.content);

                jSith.forEach(event.organization, function (_, org) {
                    let orgId = parseInt(org.organization_id);
                    let eventId = parseInt(event.event_id);
                    if (!ret.orgToEvents[orgId]) {
                        ret.orgToEvents[orgId] = {};
                    }
                    ret.orgToEvents[orgId][eventId] = true;

                    org.evBillId = id;
                    org.organization_id = orgId; //id as integer

                    ret.organization.push(org);
                });
            });

            ret.organization = S25Util.array.uniqueByProp(ret.organization, "organization_id");

            const eventId = billDefn.events[0];

            jSith.forEach(billing.lineItems, function (_, lineItem) {
                lineItem = S25Util.prettifyJson(S25Util.camelToSnakeObj(lineItem), {
                    item_id: "bill_item_id",
                    item_name: "bill_item_name",
                    item_type: "bill_item_type_id",
                    profile_id: "ev_dt_profile_id",
                    total: "total_charge",
                    tax: "total_tax",
                });

                lineItem.tax = lineItem.taxes ? Object.values(lineItem.taxes) : [];
                delete lineItem.taxes;

                lineItem.occurrences = lineItem.occurrences
                    ? Object.values(lineItem.occurrences).map((row: LineItemI) => ({
                          occurrence: occNames[row.rsrv_id],
                          adjustmentAmt: row.adjustment_amt,
                          adjustmentPercent: row.adjustment_percent,
                          adjustmentName: row.adjustment_name,
                          listPrice: row.list_price,
                          price: row.price,
                          rsrvId: row.rsrv_id,
                          total: row.total_charge,
                      }))
                    : [];

                lineItem.taxable_amt = lineItem.taxable_amt || lineItem.price;

                lineItem.eventId = eventId;
                lineItem.eventLocator = eventIdMap[lineItem.event_id]?.event_locator;
                lineItem.evBillId = id;

                if (S25Util.isDefined(lineItem.adjustment_percent)) {
                    lineItem.adjustment_percent = lineItem.adjustment_percent / 100 + "";
                }
                if (S25Util.isDefined(lineItem.adjustment_amt)) {
                    lineItem.adjustment_amt += "";
                }

                lineItem.charge_to_name = orgNames[lineItem.charge_to_id];
                lineItem.rate_group_name = groupNames[lineItem.rate_group_id];
                lineItem.rate_name = rateNames[lineItem.rate_id];
                lineItem.bill_profile_name = profileNames[lineItem.ev_dt_profile_id] || "";

                ret.bill_item.push(lineItem);
            });

            jSith.forEach(billing.adjustments, function (_, adjustment) {
                adjustment = S25Util.camelToSnakeObj(adjustment);
                adjustment.eventId = eventId; //add an event id here so we can tie it back to event when making pricing data from set
                adjustment.bill_item_id = adjustment.item_id;
                adjustment.bill_item_type_id = -1;
                adjustment.ev_dt_profile_id = -2;

                adjustment.total_tax = adjustment.total_tax || adjustment.tax;
                adjustment.tax = adjustment.taxes;
                delete adjustment.taxes;

                adjustment.evBillId = id;

                if (S25Util.isDefined(adjustment.adjustment_percent)) {
                    adjustment.adjustment_percent = adjustment.adjustment_percent / 100 + "";
                }
                if (S25Util.isDefined(adjustment.adjustment_amt)) {
                    adjustment.adjustment_amt += "";
                }

                adjustment.charge_to_name = orgNames[adjustment.charge_to_id];

                ret.bill_item.push(adjustment);
            });

            return ret;
        });
    }

    @Timeout
    public static getPricing(
        eventId: number,
        combineRelatedEvents: boolean,
        storeInstance?: InstanceType<typeof S25PricingStore>,
    ) {
        var i = 0;
        //billing: bill items
        //text: old invoice data
        //history: bill date
        //customers: organizations on event
        var includes: EventIncludeOption[] = ["billing", "text", "history", "customers"];
        !combineRelatedEvents && includes.push("relationships"); //needed to show msg to user that related events exist if they wish to combine
        if (combineRelatedEvents) {
            return EventService.getEventRelationships(eventId).then(function (rel) {
                if (rel && rel.content && rel.content.length) {
                    var eventIds = [eventId];
                    for (i = 0; i < rel.content.length; i++) {
                        eventIds.push(rel.content[i].content_event_id);
                    }

                    return S25Util.all({
                        events: EventService.getEventsInclude(eventIds, includes),
                        eventsNeedingRefresh: EventService.getEventsNeedingRefresh(eventIds),
                    }).then(function (resp) {
                        storeInstance?.setLocatorMap(resp?.events ?? []);
                        var contextEvent = S25Util.propertyGetParentWithChildValue(resp.events, "event_id", eventId);
                        var relatedEvents = [];
                        for (i = 0; i < resp.events.length; i++) {
                            if (resp.events[i] && parseInt(resp.events[i].event_id) !== eventId) {
                                relatedEvents.push(resp.events[i]);
                            }
                        }

                        if (contextEvent) {
                            var orgToEvents: any = {}; //hash to track which orgs contain which events in the context and related events
                            if (!contextEvent.bill_item) {
                                contextEvent.bill_item = [];
                            }
                            if (!contextEvent.organization) {
                                contextEvent.organization = [];
                            }

                            jSith.forEach(contextEvent.bill_item, function (_, item) {
                                item.eventId = parseInt(contextEvent.event_id);
                                item.eventLocator = contextEvent.event_locator;
                            });

                            jSith.forEach(contextEvent.organization, function (_, org) {
                                //context orgs to context event
                                var orgId = parseInt(org.organization_id);
                                var eventId = parseInt(contextEvent.event_id);
                                if (!orgToEvents[orgId]) {
                                    orgToEvents[orgId] = {};
                                    orgToEvents[orgId][eventId] = true;
                                } else {
                                    orgToEvents[orgId][eventId] = true;
                                }
                            });

                            for (i = 0; i < relatedEvents.length; i++) {
                                var relatedEvent = relatedEvents[i];

                                relatedEvent.bill_item = relatedEvent.bill_item || [];
                                jSith.forEach(relatedEvent.bill_item, function (_, item) {
                                    item.eventId = parseInt(relatedEvent.event_id);
                                    item.eventLocator = relatedEvent.event_locator;
                                });
                                contextEvent.bill_item = contextEvent.bill_item.concat(relatedEvent.bill_item);

                                relatedEvent.organization = relatedEvent.organization || [];
                                jSith.forEach(relatedEvent.organization, function (_, org) {
                                    //related event org to related event
                                    var orgId = parseInt(org.organization_id);
                                    var eventId = parseInt(relatedEvent.event_id);
                                    if (!orgToEvents[orgId]) {
                                        orgToEvents[orgId] = {};
                                        orgToEvents[orgId][eventId] = true;
                                    } else {
                                        orgToEvents[orgId][eventId] = true;
                                    }
                                });
                                contextEvent.organization = contextEvent.organization.concat(relatedEvent.organization);
                            }
                            contextEvent.organization = S25Util.array.uniqueByProp(
                                contextEvent.organization,
                                "organization_id",
                            );
                            contextEvent.orgToEvents = orgToEvents;
                            contextEvent.eventIds = eventIds;
                            contextEvent.eventsNeedingRefresh = resp.eventsNeedingRefresh;
                            return contextEvent;
                        }
                    });
                } else {
                    return S25Util.all({
                        eventData: EventService.getEventInclude(eventId, includes),
                        eventsNeedingRefresh: EventService.getEventsNeedingRefresh([eventId]),
                    }).then(function (resp) {
                        resp.eventData.eventIds = [eventId];
                        resp.eventData.eventsNeedingRefresh = resp.eventsNeedingRefresh;
                        return resp.eventData;
                    });
                }
            });
        } else {
            return S25Util.all({
                eventData: EventService.getEventInclude(eventId, includes),
                eventsNeedingRefresh: EventService.getEventsNeedingRefresh([eventId]),
            }).then(function (resp) {
                resp.eventData.eventIds = [eventId];
                resp.eventData.eventsNeedingRefresh = resp.eventsNeedingRefresh;
                return resp.eventData;
            });
        }
    }

    //put a sub total pricing item to an event (these items are located in the footer of an org list)
    public static putPricingSubtotalItem(
        eventData: any,
        orgId: number,
        isNew: boolean,
        adjustmentValue: any,
        adjustmentType: any,
        objId: number,
        eventId: number,
        reason: string,
        evBillId: number,
    ) {
        var op = isNew ? "add" : "update";
        return PricingService.putPricingLineItem(
            eventData,
            {
                bill_item_id: isNew ? null : objId,
            },
            null,
            null,
            adjustmentValue,
            adjustmentType,
            {
                organization_id: orgId,
            },
            eventId,
            reason,
            op,
            true,
            evBillId,
        );
    }

    //put pricing line item to event (these are located in an org list's body)
    @Timeout
    public static putPricingLineItem(
        eventData: any,
        billItem: any,
        rateGroupId: number,
        rateScheduleId: number,
        adjustmentValue: string,
        adjustmentType: any,
        orgObj: any,
        eventId: number,
        reason: any,
        opOverride: any,
        isAdjustment: true,
        evBillId: number,
    ) {
        var billItemId = billItem.bill_item_id;
        var billItemTypeId = billItem.bill_item_type_id;
        var profileId = billItem.ev_dt_profile_id;
        var chargeToId = orgObj.organization_id > 0 ? orgObj.organization_id : null;
        var adjustmentName = reason;
        rateGroupId = rateGroupId > 0 ? rateGroupId : null;
        var rateId = rateScheduleId > 0 ? rateScheduleId : null;

        var adjustmentPercent,
            adjustmentAmt,
            op = opOverride;

        //ANG-4959 force delete of existing GLOBAL adjustments when there's no comment and the value is 0
        if (isAdjustment && !parseFloat(adjustmentValue) && !reason && !!billItemId) {
            return PricingService.deletePricingAdjustment(eventId, evBillId, billItemId);
        }

        if (adjustmentType === "percentage") {
            adjustmentPercent = adjustmentValue;
        } else {
            adjustmentAmt = adjustmentValue;
        }

        var billItemArr = S25Util.propertyGet(eventData, "bill_item") || [];
        if (!op && billItemArr.length > 0) {
            //actually replace billItem with the newBillItem
            op = "update";
        } else if (!op) {
            op = "add";
        }

        let payload: any = {
            content: {
                data: [
                    {
                        op: op,
                        lineItems: [
                            {
                                profileId: profileId,
                                itemId: billItemId,
                                itemType: billItemTypeId,
                            },
                        ],
                    },
                ],
            },
        };

        let lineItem = payload.content.data[0].lineItems[0];
        S25Util.extend(lineItem, {
            chargeToId: chargeToId,
            adjustmentName: adjustmentName,
            rateGroupId: rateGroupId,
            rateId: rateId,
            adjustmentAmt: parseFloat(adjustmentAmt),
            adjustmentPct: parseFloat(adjustmentPercent),
        });

        if (isAdjustment) {
            payload.content.data[0].adjustments = payload.content.data[0].lineItems;
            payload.content.data[0].adjustments[0].billId = evBillId;
            delete payload.content.data[0].lineItems;
        } else {
            payload.content.data[0].lineItems[0].billId = evBillId;
        }

        evBillId && payload.content.data.push({ billIdList: [evBillId] });

        payload = S25Util.deleteUndefDeep(payload);

        if (evBillId) {
            TelemetryService.sendWithSub("Pricing", "PaymentMode", "AddAdjustment");
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/billingCustom.json?include=occurrences&expand=T",
                    "PricingService.putPricingLineItem",
                ),
                payload,
            );
        } else {
            TelemetryService.sendWithSub("Pricing", "StandardMode", "AddAdjustment");
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/" + eventId + "/billing.json?include=occurrences&expand=T",
                    "PricingService.putPricingLineItem",
                ),
                payload,
            );
        }
    }

    // Delete a subtotal adjustment
    @Timeout
    public static deletePricingAdjustment(eventId: number, evBillId: number, adjustmentId: number) {
        if (evBillId) {
            let payload = {
                content: {
                    data: [
                        { op: "remove", adjustments: [{ itemId: adjustmentId, billId: evBillId }] },
                        { billIdList: [evBillId] },
                    ],
                },
            };
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/billingCustom.json?include=occurrences&expand=T",
                    "PricingService.deletePricingAdjustment",
                ),
                payload,
            );
        }

        let payload = { content: { data: [{ op: "remove", adjustments: [{ itemId: adjustmentId }] }] } };
        return DataAccess.put(
            DataAccess.injectCaller(
                `/micro/event/${eventId}/billing.json?include=occurrences&expand=T`,
                "PricingService.deletePricingAdjustment",
            ),
            payload,
        );
    }

    public static putPricingLineItemAttr(
        eventId: number,
        attrName: string,
        attrValue: any,
        billItemId: number,
        billItemTypeId: number,
        profileId: number,
        evBillId: number,
    ) {
        let payload: any = {
            content: {
                data: [
                    {
                        op: "update",
                        lineItems: [{ profileId: profileId, itemId: billItemId, itemType: billItemTypeId }],
                    },
                ],
            },
        };
        payload.content.data[0].lineItems[0][attrName] = attrValue;

        if (evBillId) {
            payload.content.data[0].lineItems[0].billId = evBillId;
            payload.content.data.push({ billIdList: [evBillId] });
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/billingCustom.json?include=occurrences+rate_info+profiles&expand=T",
                    "PricingService.putPricingLineItemAttr",
                ),
                payload,
            );
        } else {
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/" + eventId + "/billing.json?include=occurrences+rate_info+profiles&expand=T",
                    "PricingService.putPricingLineItemAttr",
                ),
                payload,
            );
        }
    }

    public static putPricingLineItemChargeToId(
        eventId: number,
        chargeToId: number,
        billItemId: number,
        billItemTypeId: number,
        profileId: number,
        evBillId: number,
    ) {
        return PricingService.putPricingLineItemAttr(
            eventId,
            "chargeToId",
            chargeToId,
            billItemId,
            billItemTypeId,
            profileId,
            evBillId,
        );
    }

    public static putPricingLineItemRateGroup(
        eventId: number,
        rateGroupId: number,
        billItemId: number,
        billItemTypeId: number,
        profileId: number,
        evBillId: number,
    ) {
        return PricingService.putPricingLineItemAttr(
            eventId,
            "rateGroupId",
            rateGroupId,
            billItemId,
            billItemTypeId,
            profileId,
            evBillId,
        );
    }

    public static putPricingReservationItem(
        eventId: number,
        profileId: number,
        rsrvId: number,
        billItemId: number,
        billItemType: number,
        adjustmentType: "dollarAmt" | "percentage",
        adjustmentValue: number,
        description: string,
        evBillId: number,
        isNew: boolean,
    ) {
        let payload: any = {
            content: {
                data: [
                    {
                        op: isNew ? "add" : "update",
                        lineItems: [
                            {
                                profileId: profileId,
                                rsrvId: rsrvId,
                                itemId: billItemId,
                                itemType: billItemType,
                                adjustmentName: description,
                            },
                        ],
                    },
                ],
            },
        };

        if (adjustmentType === "percentage") {
            payload.content.data[0].lineItems[0].adjustmentPct = adjustmentValue;
        } else {
            payload.content.data[0].lineItems[0].adjustmentAmt = adjustmentValue;
        }

        if (evBillId) {
            payload.content.data[0].lineItems[0].billId = evBillId;
            payload.content.data.push({ billIdList: [evBillId] });
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/billingCustom.json?include=occurrences+rate_info+profiles&expand=T",
                    "PricingService.putPricingReservationItem",
                ),
                payload,
            );
        } else {
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/" + eventId + "/billing.json?include=occurrences+rate_info+profiles&expand=T",
                    "PricingService.putPricingReservationItem",
                ),
                payload,
            );
        }
    }
    public static putPricingEventRateGroup(eventId: number, rateGroupId: number, evBillId: number) {
        let payload: any = { content: { data: [{ op: "all", fields: [{ rateGroupId: rateGroupId }] }] } };

        if (evBillId) {
            payload.content.data[0].fields[0].billId = evBillId;
            payload.content.data.push({ billIdList: [evBillId] });
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/billingCustom.json?include=occurrences+rate_info+profiles&expand=T",
                    "PricingService.putPricingEventRateGroup",
                ),
                payload,
            );
        } else {
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/" + eventId + "/billing.json?include=occurrences+rate_info+profiles&expand=T",
                    "PricingService.putPricingEventRateGroup",
                ),
                payload,
            );
        }
    }

    public static putBillingCustom(updateData: Partial<InvoiceUpdateData>, evBillId: number): Promise<Invoice> {
        const payload = {
            content: {
                data: [
                    {
                        op: "update",
                        billId: evBillId,
                        ...updateData,
                    },
                ],
            },
        };
        return DataAccess.put(
            DataAccess.injectCaller(
                "/micro/event/billingCustom.json?include=occurrences",
                "PricingService.putBillingCustom",
            ),
            payload,
        );
    }

    public static putMultiInvoices(invoiceData: Partial<InvoiceUpdateData>[]) {
        return DataAccess.put(
            DataAccess.injectCaller("/micro/event/billingCustom.json", "PricingService.putMultiInvoices"),
            { content: { data: invoiceData } },
        );
    }

    public static putInvoiceId(evBillId: number, orgId: number, invoiceId: string) {
        const payload = {
            content: {
                data: [
                    {
                        invoiceDetails: [{ billId: evBillId, acctId: orgId, invoiceId: invoiceId }],
                    },
                ],
            },
        };
        return DataAccess.put(
            DataAccess.injectCaller("/micro/event/billingCustom.json", "PricingService.putInvoiceId"),
            payload,
        );
    }

    //put modified pricing bill date to event (located in the summary block at the top of the pricing component)
    public static putPricingBillingDate(eventId: number, newDate: Date, evBillId: number) {
        if (evBillId) {
            return PricingService.putBillingCustom(
                { billDate: S25Util.date.toS25ISODateStrEndOfDay(S25Util.date.getDate(newDate)) },
                evBillId,
            );
        } else {
            const payload = {
                content: {
                    data: [
                        {
                            op: "all",
                            fields: [{ billDate: S25Util.date.toS25ISODateStrEndOfDay(S25Util.date.getDate(newDate)) }],
                        },
                    ],
                },
            };
            return DataAccess.put(
                DataAccess.injectCaller(
                    "/micro/event/" + eventId + "/billing.json",
                    "PricingService.putPricingBillingDate",
                ),
                payload,
            );
        }
    }

    @Timeout
    public static getStandardPricingForEvent(eventIdArr: number[]) {
        return DataAccess.get<{
            content: { data: { items: Invoice[] }; id: number; updated: ISODateString };
        }>(
            DataAccess.injectCaller(
                "/micro/event/" + eventIdArr.join("+") + "/billing.json",
                "PricingService.getStandardPricingForEvent",
            ),
        ).then(function (data) {
            return (data && data.content && data.content.data && data.content.data.items) || [];
        });
    }

    @Timeout
    public static getStandardPricingForEventIncludeRateInfo(eventIdArr: number[]) {
        return DataAccess.get<{ content: MicroBilling }>(
            DataAccess.injectCaller(
                "/micro/event/" +
                    eventIdArr.join("+") +
                    "/billing.json?include=rate_info+organizations+profiles+occurrences&expand=T",
                "PricingService.getStandardPricingForEvent",
            ),
        ).then(function (data) {
            return data.content;
        });
    }

    @Timeout
    public static getPricingSetsForEvents(
        eventIds: (number | NumericalString)[],
        storeInstance?: InstanceType<typeof S25PricingStore>,
    ): Promise<any> {
        return DataAccess.get<{
            content: {
                data: {
                    requestId: number;
                    items: Invoice[];
                };
            };
        }>(
            DataAccess.injectCaller(
                "/micro/event/billingCustom.json?eventId=" +
                    eventIds.join("+") +
                    "&include=rate_info+occurrences+requirements+event_type&expand=T",
                "PricingService.getPricingSetsForEvents",
            ),
        ).then(
            function (data): { content: { data: { requestId: number; items: Invoice[] } } } | {} {
                storeInstance?.setInvoices(S25Util.deepCopy(data?.content?.data?.items ?? []));
                return (data && data.content) || {};
            },
            function (): [] {
                return [];
            },
        );
    }

    @Timeout
    public static getPricingSet(evBillId: number, storeInstance?: InstanceType<typeof S25PricingStore>) {
        return DataAccess.get<{ content: MicroBilling }>(
            DataAccess.injectCaller(
                `/micro/event/billingCustom.json?billId=${evBillId}&include=rate_info+organizations+profiles+occurrences&expand=T`,
                "PricingService.getPricingSet",
            ),
        ).then(async function (data) {
            const eventData = await EventMicroService.getEventDetailById(
                S25Util.array.forceArray(data?.content?.data?.items?.[0]?.billDefn?.events),
                "F",
                "reservations",
            );
            storeInstance?.setOccurrences(
                eventData?.content?.data?.items?.flatMap((item) =>
                    item.profiles?.flatMap((profile) =>
                        profile.reservations?.map((rsrv: Occurrence) => ({
                            ...rsrv,
                            eventId: item.id,
                            occurrence: rsrv.rsrvStartDt,
                        })),
                    ),
                ) ?? [],
            );
            return data?.content;
        });
    }

    @Timeout
    public static deletePricingSet(evBillId: number) {
        TelemetryService.sendWithSub("Pricing", "Event", "SetDelete");
        return DataAccess.delete(
            DataAccess.injectCaller(
                "/micro/event/" + evBillId + "/billingCustom.json",
                "PricingService.deletePricingSet",
            ),
        );
    }

    @Timeout
    public static postBillingCustom(
        name: string,
        eventIds: any,
        rsrvIds: any,
        reqIds: any,
        includeEventType: string,
        dateRange?: { start: Date; end: Date },
    ) {
        const includeSpacesResources = !!rsrvIds?.length || !!dateRange;
        const payload = {
            content: {
                data: [
                    {
                        op: "params",
                        eventIdList: eventIds,
                        ...(rsrvIds && { rsrvIdList: rsrvIds }),
                        includeRequirements: reqIds?.length > 0,
                        includeSpaces: includeSpacesResources,
                        includeResources: includeSpacesResources,
                        requirementIdList: reqIds,
                        includeEventType: includeEventType,
                        billName: name,
                        ...(dateRange && { startDt: dateRange.start, endDt: dateRange.end }),
                    },
                ],
            },
        };
        return DataAccess.post(
            DataAccess.injectCaller("/micro/event/billingCustom.json", "PricingService.postBillingCustom"),
            payload,
        );
    }

    // creates an invoice copy of standard pricing
    public static async postStandardInvoice(eventPricingData: EventPricingData) {
        const rsrvIds = eventPricingData.occurrences?.map((occ) => occ.rsrvId) ?? [];
        let reqIds: number[] = [];
        let subtotalAdjustments: LineItem[] = [];
        let payments: Partial<ICheckoutRequest>[] = [];

        eventPricingData.bill_item?.forEach((lineItem) => {
            const {
                adjustment_amt,
                adjustment_percent,
                adjustment_name,
                bill_item_id,
                bill_item_type_id,
                charge_to_id,
                paymentMapAction,
                total_charge,
            } = lineItem;

            if (bill_item_type_id === lineItemType.ADJUSTMENT) {
                if (!paymentMapAction || paymentMapAction.action === "map") {
                    subtotalAdjustments.push({
                        adjustmentAmt: adjustment_amt,
                        adjustmentPct:
                            adjustment_percent &&
                            (typeof adjustment_percent === "string"
                                ? +adjustment_percent.replace("%", "")
                                : adjustment_percent * 100),
                        adjustmentName: adjustment_name,
                        chargeToId: charge_to_id,
                    });
                } else if (paymentMapAction.action === "convert") {
                    payments.push({
                        productDescription: adjustment_name ?? "",
                        amountInCents: Math.abs((total_charge ?? 0) * 100), // if an adjustment representing a payment is negative, convert to positive payment value to deduct from balance
                        currency: "usd",
                        organizationId: charge_to_id,
                        eventId: eventPricingData.event_id,
                        type: paymentMapAction.type.toUpperCase(),
                        source: "manual",
                        paymentStatus: paymentMapAction.status,
                        dueDate: S25Util.date.toS25ISODateStrStartOfDay(paymentMapAction.dueDate ?? new Date()),
                        ...(paymentMapAction.datePaid && {
                            paymentSuccess: S25Util.date.toS25ISODateStrStartOfDay(paymentMapAction.datePaid),
                        }),
                    });
                }
            }
            if (bill_item_type_id === lineItemType.REQUIREMENT) reqIds.push(bill_item_id);
        });

        const newInvoice = await PricingService.postBillingCustom(
            S25PricingUtil.StandardInvoiceName,
            eventPricingData.eventIds,
            rsrvIds,
            reqIds,
            "true",
        );

        const newInvoiceId = newInvoice?.content?.data?.items[0]?.id;
        let promiseArr: Promise<void>[] = [];
        if (newInvoiceId) {
            if (subtotalAdjustments.length) {
                let payload = {
                    content: {
                        data: [
                            {
                                op: "add",
                                adjustments: subtotalAdjustments.map((adj) => ({ ...adj, billId: newInvoiceId })) ?? [],
                            },
                            {
                                billIdList: [newInvoiceId],
                            },
                        ],
                    },
                };
                payload = S25Util.deleteUndefDeep(payload);
                promiseArr.push(
                    DataAccess.put(
                        DataAccess.injectCaller(
                            "/micro/event/billingCustom.json?include=occurrences&expand=T",
                            "PricingService.postStandardInvoice",
                        ),
                        payload,
                    ),
                );
            }
            await Promise.all([
                ...promiseArr,
                ...payments.map((payment) =>
                    PaymentService.createPayment({ ...payment, evBillId: newInvoiceId } as ICheckoutRequest),
                ),
            ]);
        }
    }

    public static putLineItemAdjustments(lineItems: LineItem[]) {
        const payload = {
            content: {
                data: [
                    {
                        op: "update",
                        lineItems: lineItems,
                    },
                ],
            },
        };
        return DataAccess.put(
            DataAccess.injectCaller("/micro/event/billingCustom.json", "PricingService.putLineItems"),
            payload,
        );
    }

    public static putInvoiceSortOrder(eventId: number, invoiceSortData: InvoiceSortGroup[]) {
        const payload = {
            content: {
                data: {
                    items: [
                        {
                            id: eventId,
                            sortGroup: invoiceSortData,
                        },
                    ],
                },
            },
        };
        return DataAccess.put<InvoiceSort>(
            DataAccess.injectCaller(
                `/micro/event/billing/sort.json?event_id=${eventId}`,
                "PricingService.putInvoiceSortOrder",
            ),
            payload,
        );
    }

    public static async getInvoices(
        eventId: number,
        combineRelated: boolean,
        storeInstance?: InstanceType<typeof S25PricingStore>,
    ): Promise<MicroBilling> {
        try {
            let eventIds = [eventId];
            if (combineRelated) {
                const relatedIds = await EventService.getRelatedEventIds(eventId);
                eventIds = S25Util.array.unique([...eventIds, ...relatedIds]);
            }
            return await PricingService.getPricingSetsForEvents(eventIds, storeInstance);
        } catch (err) {
            console.error(err);
            S25Util.showError(err);
        }
    }
}
